import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Observable, Subject, filter, map, shareReplay, takeUntil } from 'rxjs';
import { CacheService } from './shared/services/cache.service';
import { AppService } from './shared/services/app.service';
//import { LocalReportsMigrationService } from './report-form/shared/services/local-reports-migration-service';
import { FrLogService } from './shared/services/fr-log.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  private msalBroadcastService = inject(MsalBroadcastService);
  private authService = inject(MsalService);
  private breakpointObserver = inject(BreakpointObserver);
  private appService = inject(AppService);
  private router = inject(Router);
  private cacheService = inject(CacheService);
  private frLogService = inject(FrLogService);

  isIframe = false;
  loggedIn = false;
  private readonly _destroying$ = new Subject<void>();

  showVersionInfo = false;

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay()
    );

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe({
        next: () => {
          this.setLoginDisplay();
          this.checkAndSetActiveAccount();
        },
        error: (err) => {
          console.error(err);
        }
      });
    this.appService.notifyOnUpdate();
    setTimeout(() => this.cacheService.loadAndCacheDictionaries(), 5000);
  }

  setLoginDisplay() {
    this.loggedIn = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }

    activeAccount = this.authService.instance.getActiveAccount();

    if (environment.headerEnvironmentInfo === 'TEST') {
      this.frLogService.logFrTrace(`${activeAccount?.homeAccountId} | User logged in.`);
    }

    const isReporter =
      activeAccount?.idTokenClaims?.roles?.includes('Reporter') ?? false;
    const isReader =
      activeAccount?.idTokenClaims?.roles?.includes('Reader') ?? false;
    const isInstructor =
      activeAccount?.idTokenClaims?.roles?.includes('Instructor') ?? false;
    const isAllReportsReader =
      activeAccount?.idTokenClaims?.roles?.includes('AllReportsReader') ??
      false;

    this.showVersionInfo = isAllReportsReader;

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event as NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        if (isReporter && !isReader && !isInstructor && !isAllReportsReader) {
          if (
            event.url === '/my-reports' ||
            event.url.startsWith('/my-reports/report/') ||
            event.url.startsWith('/report-form')
          ) {
            return;
          }
          this.router.navigate(['/my-reports']);
        } else if (
          !isReporter &&
          isReader &&
          !isInstructor &&
          !isAllReportsReader
        ) {
          if (event.url.startsWith('/admin/report/')) {
            return;
          }
          this.router.navigate(['/admin']);
        }
      });
  }

  logout(): void {
    this.authService.logoutRedirect();
  }
}
